import { Tooltip } from '@/components/ui/tooltip';
import { Box, Button, Flex, Text, chakra } from '@chakra-ui/react';
import type { DashboardDecisionSourceKey, Decision, DecisionDay } from '@fluenceur/types';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { DecisionDayDetail } from './DecisionDayDetail';
import { DecisionTooltip } from './DecisionTooltip';
import {
  LEVEL_COLORS,
  formatCalendarDay,
  formatDay,
  levelOf,
  nextThreeDays,
} from './decision-format';

/** How many days the strip lays out while it loads. */
const LOADING_DAYS = 14;

export interface DecisionStripProps {
  /**
   * `page`: the UGC page, taller cells, tooltip above, no type.
   * `dashboard`: compact cells, tooltip below, each decision's type, a link to every collab.
   */
  variant: 'page' | 'dashboard';
  /** The server's fourteen days, today first. Empty while loading. */
  days: DecisionDay[];
  isLoading?: boolean;
  /** The sources that could not be read: named in a banner, the others still laid. */
  failedSources?: DashboardDecisionSourceKey[];
  onRetry?: () => void;
  /** The day whose decisions are listed under the strip, `YYYY-MM-DD`, or null. */
  selected: string | null;
  /** A click on a day: its date, or null when it was the selected one. */
  onSelect: (date: string | null) => void;
  /** Opens a listed decision in place; without it, each one links to its page. */
  onOpenDecision?: (decision: Decision) => void;
}

/** Where a day's tooltip opens: above on the UGC page, below on the dashboard, held in at the edges. */
const placementOf = (variant: DecisionStripProps['variant'], index: number, total: number) => {
  const side = variant === 'page' ? 'top' : 'bottom';
  if (index < 2) return `${side}-start` as const;
  if (index >= total - 2) return `${side}-end` as const;
  return side;
};

/**
 * The « Décisions sur 14 jours » strip, for any decision: one cell per day from
 * today in the brand's timezone, the count written in it and the shade warming
 * as decisions pile up. Hovering or focusing a day opens a card listing what to
 * decide; a click selects the day (the UGC page filters its table on it) and
 * lists its decisions under the strip, a second click closes it. Every cell is
 * a button read as « 15 octobre : 3 décisions ». Scrolls sideways on a phone.
 */
export const DecisionStrip = ({
  variant,
  days,
  isLoading = false,
  failedSources = [],
  onRetry,
  selected,
  onSelect,
  onOpenDecision,
}: DecisionStripProps) => {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const locale = i18n.language;
  const isDashboard = variant === 'dashboard';
  const cellHeight = isDashboard ? '32px' : '40px';
  const total = days.reduce((sum, day) => sum + day.decisions.length, 0);
  const next = nextThreeDays(days);
  const selectedDay = days.find((day) => day.date === selected) ?? null;

  return (
    <Box
      as="section"
      aria-labelledby={titleId}
      p="12px 14px"
      borderWidth="1px"
      borderColor="cardBorder"
      rounded="16px"
      bg="panelBg"
      data-testid="decision-strip"
      data-variant={variant}
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'baseline' }}
        direction={{ base: 'column', md: 'row' }}
        gap={{ base: '4px', md: '12px' }}
        mb="10px"
      >
        <Flex align="baseline" gap="10px" wrap="wrap" minW="0">
          <Text id={titleId} fontFamily="heading" fontSize="14px" fontWeight={600} color="ink">
            {t(`decisionStrip.title.${variant}`)}
          </Text>
          {isLoading ? null : total === 0 ? (
            <Text fontSize="12.5px" color="inkMuted" data-testid="decision-strip-empty">
              {t(`decisionStrip.empty`)}
            </Text>
          ) : (
            <Text fontSize="12.5px" color="inkMuted" data-testid="decision-strip-summary">
              {next === 0
                ? t(`decisionStrip.summary.none`)
                : t(`decisionStrip.summary.next`, { count: next })}
            </Text>
          )}
        </Flex>
      </Flex>

      {failedSources.length > 0 ? (
        <Flex
          align="center"
          justify="space-between"
          gap="10px"
          mb="10px"
          px="12px"
          py="8px"
          rounded="10px"
          borderWidth="1px"
          borderColor="border.emphasized"
          bg="background"
          data-testid="decision-strip-failed"
          role="status"
        >
          <Text fontSize="12.5px" color="ink">
            {failedSources.map((source) => t(`decisionStrip.failed.${source}`)).join(' · ')}
          </Text>
          {onRetry ? (
            <Button size="xs" variant="outline" onClick={onRetry}>
              {t(`decisionStrip.retry`)}
            </Button>
          ) : null}
        </Flex>
      ) : null}

      <Box overflowX="auto" pb="2px" mx="-2px" px="2px">
        {isLoading ? (
          <Flex
            gap="4px"
            minW="max-content"
            w="full"
            aria-busy="true"
            data-testid="decision-strip-loading"
          >
            {Array.from({ length: LOADING_DAYS }, (_, index) => (
              <Flex key={index} direction="column" gap="4px" flex="1 0 36px">
                <Box h="32px" />
                <Box
                  h={cellHeight}
                  rounded="8px"
                  bg="border.subtle"
                  animation="pulse 1.5s ease-in-out infinite"
                  data-testid="decision-strip-loading-cell"
                />
              </Flex>
            ))}
          </Flex>
        ) : (
          <Flex gap="4px" minW="max-content" w="full">
            {days.map((day, index) => {
              const count = day.decisions.length;
              const level = levelOf(count);
              const isSelected = day.date === selected;
              const isToday = index === 0;
              return (
                <Flex key={day.date} direction="column" align="stretch" gap="4px" flex="1 0 36px">
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    h="32px"
                    rounded="8px"
                    bg={isToday ? 'darkNav' : undefined}
                    color={isToday ? 'white' : 'inkMuted'}
                  >
                    {isToday ? (
                      <Text fontSize="11px" fontWeight={600} data-testid="decision-strip-today">
                        {t(`decisionStrip.today`)}
                      </Text>
                    ) : (
                      <>
                        <Text fontSize="10.5px" lineHeight="1.2" whiteSpace="nowrap">
                          {formatDay(day.date, locale, { weekday: 'short' })}
                        </Text>
                        <Text fontSize="12px" color="ink" lineHeight="1.2">
                          {formatDay(day.date, locale, { day: 'numeric' })}
                        </Text>
                      </>
                    )}
                  </Flex>
                  <Tooltip
                    disabled={count === 0}
                    openDelay={150}
                    closeDelay={50}
                    positioning={{ placement: placementOf(variant, index, days.length) }}
                    contentProps={{
                      bg: 'transparent',
                      color: 'ink',
                      p: '0',
                      maxW: 'none',
                      boxShadow: 'none',
                    }}
                    content={<DecisionTooltip day={day} isToday={isToday} showType={isDashboard} />}
                  >
                    <chakra.button
                      type="button"
                      h={cellHeight}
                      rounded="8px"
                      textAlign="center"
                      cursor="pointer"
                      fontFamily="heading"
                      fontSize="14px"
                      fontWeight={600}
                      {...LEVEL_COLORS[level]}
                      // Rings drawn with a border, never a shadow (house rule).
                      borderWidth={isSelected ? '2px' : '1px'}
                      borderColor={
                        isSelected ? 'darkNav' : isToday ? 'rgba(23, 23, 21, 0.35)' : 'transparent'
                      }
                      _hover={{ filter: 'brightness(0.97)' }}
                      _focusVisible={{ outline: '2px solid', outlineColor: 'border.emphasized' }}
                      aria-pressed={isSelected}
                      aria-label={t(`decisionStrip.dayLabel`, {
                        date: formatCalendarDay(day.date, locale),
                        count,
                      })}
                      data-count={String(count)}
                      data-level={String(level)}
                      data-date={day.date}
                      onClick={() => onSelect(isSelected ? null : day.date)}
                    >
                      <span aria-hidden="true">{count > 0 ? count : ''}</span>
                    </chakra.button>
                  </Tooltip>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>

      {selectedDay && selectedDay.decisions.length > 0 ? (
        <DecisionDayDetail
          day={selectedDay}
          showType={isDashboard}
          onOpenDecision={onOpenDecision}
        />
      ) : null}
    </Box>
  );
};
